import React from "react";
import { Grid, GridColumn, GridToolbar } from "@progress/kendo-react-grid";
import { ExcelExport, ExcelExportColumn } from "@progress/kendo-react-excel-export";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { process } from "@progress/kendo-data-query";

// Quick date-range presets (client 2026-09-25). Selecting one fills the
// Start/End pickers with the computed range; editing a picker by hand
// drops back to "All Dates". Rolling windows include today.
const QUICK_RANGES = [
  { text: "All Dates", value: "" },
  { text: "Today", value: "today" },
  { text: "Yesterday", value: "yesterday" },
  { text: "Last 3 Days", value: "3days" },
  { text: "Last 7 Days", value: "7days" },
  { text: "Last Month", value: "month" }
];

// Fulfillment status filter (client 2026-10-01) — lets fulfillment hide
// what's already shipped. Values must match FabricOrder.Statuses.
const STATUS_OPTIONS = [
  { text: "All Statuses", value: "" },
  { text: "Received", value: "Received" },
  { text: "In Process", value: "In Process" },
  { text: "Shipped", value: "Shipped" },
  { text: "Declined", value: "Declined" }
];

function quickRangeDates(value) {
  var now = new Date();
  var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  function daysAgo(n) { return new Date(today.getFullYear(), today.getMonth(), today.getDate() - n); }
  switch (value) {
    case "today": return { start: today, end: today };
    case "yesterday": return { start: daysAgo(1), end: daysAgo(1) };
    case "3days": return { start: daysAgo(2), end: today };
    case "7days": return { start: daysAgo(6), end: today };
    case "month": return { start: daysAgo(29), end: today };
    default: return { start: null, end: null };
  }
}

/*
 * FabricSampleOrdersGrid — admin report for fabric sample cart orders.
 * Port of the Lee project's /Portal/Report/FabricSample grid: filter
 * toolbar (Order #, Last Name, Start/End Date), sortable/pageable grid,
 * Export to Excel (orders) and Export Cuttings Summary (one row per
 * order+fabric with quantities summed).
 * NOTE: server-side rendered — ChakraCore rules, no ?. or ?? syntax.
 */
export default class FabricSampleOrdersGrid extends React.Component {
  constructor(props) {
    super(props);
    this._export = null;
    this._exportSummary = null;
    this.state = {
      dataState: {
        skip: 0,
        take: 20,
        sort: [{ field: "dateSort", dir: "desc" }]
      },
      filters: {
        orderId: "",
        search: "",
        status: STATUS_OPTIONS[0],
        startDate: null,
        endDate: null,
        quickRange: QUICK_RANGES[0]
      }
    };
  }

  onDataStateChange = (e) => {
    this.setState({ dataState: e.dataState });
  };

  onQuickRange = (e) => {
    var selected = e.target.value;
    var range = quickRangeDates(selected ? selected.value : "");
    this.setState(prev => ({
      filters: { ...prev.filters, quickRange: selected, startDate: range.start, endDate: range.end },
      dataState: { ...prev.dataState, skip: 0 }
    }));
  };

  getFilteredData = () => {
    var data = this.props.orders || [];
    var f = this.state.filters;

    if (f.orderId) {
      var oq = f.orderId.toLowerCase();
      data = data.filter(d => (d.orderId || "").toLowerCase().indexOf(oq) > -1);
    }
    if (f.search) {
      // client 2026-10-16 (ticket 86e3re32m): one box searches name,
      // company, Fairfield account number, and email (was last name only)
      var sq = f.search.toLowerCase();
      data = data.filter(d =>
        (d.name || "").toLowerCase().indexOf(sq) > -1 ||
        (d.company || "").toLowerCase().indexOf(sq) > -1 ||
        (d.fairfieldAccountNumber || "").toLowerCase().indexOf(sq) > -1 ||
        (d.email || "").toLowerCase().indexOf(sq) > -1);
    }
    if (f.status && f.status.value) {
      data = data.filter(d => (d.status || "Received") === f.status.value);
    }
    if (f.startDate && f.endDate) {
      var start = f.startDate;
      var end = new Date(f.endDate.getFullYear(), f.endDate.getMonth(), f.endDate.getDate(), 23, 59, 59);
      data = data.filter(d => {
        if (!d.dateSort) return false;
        var dt = new Date(d.dateSort.replace(" ", "T"));
        return dt >= start && dt <= end;
      });
    }
    return data;
  };

  onFilterChange = (field, value) => {
    this.setState(prev => {
      var filters = { ...prev.filters, [field]: value };
      // hand-editing a date picker leaves preset mode
      if (field === "startDate" || field === "endDate") filters.quickRange = QUICK_RANGES[0];
      return { filters: filters, dataState: { ...prev.dataState, skip: 0 } };
    });
  };

  onRefresh = () => {
    this.setState({
      dataState: { skip: 0, take: 20, sort: [{ field: "dateSort", dir: "desc" }] },
      filters: { orderId: "", search: "", status: STATUS_OPTIONS[0], startDate: null, endDate: null, quickRange: QUICK_RANGES[0] }
    });
  };

  onExportExcel = () => {
    if (this._export) {
      this._export.save(this.getFilteredData());
    }
  };

  // Cuttings summary: one row per (order, fabric); duplicate fabric names
  // within an order are grouped and quantities summed (Lee parity).
  buildSummaryRows = () => {
    var rows = [];
    var data = this.getFilteredData();
    for (var i = 0; i < data.length; i++) {
      var o = data[i];
      var grouped = {};
      var items = o.items || [];
      for (var j = 0; j < items.length; j++) {
        var it = items[j];
        var key = it.name || it.sku;
        if (!grouped[key]) grouped[key] = 0;
        grouped[key] += it.quantity || 0;
      }
      var names = Object.keys(grouped).sort();
      for (var k = 0; k < names.length; k++) {
        rows.push({
          orderId: o.orderId,
          dateString: o.dateString,
          name: o.name,
          company: o.company,
          email: o.email,
          level: o.level,
          phone: o.phone,
          address: o.address,
          address2: o.address2,
          city: o.city,
          region: o.region,
          postalCode: o.postalCode,
          fabric: names[k],
          cuttings: grouped[names[k]]
        });
      }
    }
    // Sort: LastName, then date desc, then fabric — approximated via name
    rows.sort(function (a, b) {
      if (a.name !== b.name) return a.name < b.name ? -1 : 1;
      if (a.dateString !== b.dateString) return a.dateString < b.dateString ? 1 : -1;
      return a.fabric < b.fabric ? -1 : 1;
    });
    return rows;
  };

  onExportSummary = () => {
    if (this._exportSummary) {
      this._exportSummary.save(this.buildSummaryRows());
    }
  };

  render() {
    var f = this.state.filters;
    var filteredData = this.getFilteredData();
    var result = process(filteredData, this.state.dataState);
    var totalCuttings = 0;
    for (var i = 0; i < filteredData.length; i++) {
      totalCuttings += filteredData[i].count || 0;
    }

    return (
      <div>
        <div className="quote-history-filters">
          <div className="quote-filter-item">
            <label>Order #</label>
            <input
              type="text"
              className="k-input k-textbox k-input-md k-rounded-md k-input-solid"
              value={f.orderId}
              onChange={(e) => this.onFilterChange("orderId", e.target.value)}
              placeholder="FS-100001"
              style={{ width: "150px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>Search</label>
            <input
              type="text"
              className="k-input k-textbox k-input-md k-rounded-md k-input-solid"
              value={f.search}
              onChange={(e) => this.onFilterChange("search", e.target.value)}
              placeholder="Name, company, account #, email"
              style={{ width: "230px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>Status</label>
            <DropDownList
              data={STATUS_OPTIONS}
              textField="text"
              dataItemKey="value"
              value={f.status}
              onChange={(e) => this.onFilterChange("status", e.target.value)}
              style={{ width: "140px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>Date Range</label>
            <DropDownList
              data={QUICK_RANGES}
              textField="text"
              dataItemKey="value"
              value={f.quickRange}
              onChange={this.onQuickRange}
              style={{ width: "150px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>Start Date</label>
            <DatePicker
              value={f.startDate}
              onChange={(e) => this.onFilterChange("startDate", e.value)}
              format="MM/dd/yyyy"
              style={{ width: "160px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>End Date</label>
            <DatePicker
              value={f.endDate}
              onChange={(e) => this.onFilterChange("endDate", e.value)}
              format="MM/dd/yyyy"
              style={{ width: "160px" }}
            />
          </div>
          <div className="quote-filter-item">
            <label>&nbsp;</label>
            <span style={{ fontSize: "13px", color: "#666", lineHeight: "30px" }}>
              {filteredData.length} orders / {totalCuttings} cuttings
            </span>
          </div>
        </div>

        {/* Hidden exporter for the cuttings summary */}
        <ExcelExport
          fileName={"fabric-cuttings-summary.xlsx"}
          ref={(exporter) => { this._exportSummary = exporter; }}
        >
          <ExcelExportColumn field="orderId" title="Order#" width={90} />
          <ExcelExportColumn field="dateString" title="Date" width={140} />
          <ExcelExportColumn field="name" title="Name" width={150} />
          <ExcelExportColumn field="company" title="Company" width={150} />
          <ExcelExportColumn field="email" title="Email" width={180} />
          <ExcelExportColumn field="level" title="Level" width={100} />
          <ExcelExportColumn field="phone" title="Phone" width={110} />
          <ExcelExportColumn field="address" title="Address" width={160} />
          <ExcelExportColumn field="address2" title="Address 2" width={100} />
          <ExcelExportColumn field="city" title="City" width={110} />
          <ExcelExportColumn field="region" title="State" width={70} />
          <ExcelExportColumn field="postalCode" title="Zip" width={80} />
          <ExcelExportColumn field="fabric" title="Fabric" width={200} />
          <ExcelExportColumn field="cuttings" title="Cuttings" width={80} />
        </ExcelExport>

        <ExcelExport
          data={filteredData}
          fileName="fabric-cart-orders.xlsx"
          ref={(exporter) => { this._export = exporter; }}
        >
          <Grid
            data={result}
            {...this.state.dataState}
            onDataStateChange={this.onDataStateChange}
            sortable={true}
            pageable={{ pageSizes: [10, 20, 50] }}
            style={{ fontSize: "14px" }}
            cellRender={(cell, cellProps) => {
              if (!cell || !cell.props) return cell;
              const labels = { orderId: "Order #", dateString: "Date", name: "Name", companyAccount: "Company / Account #", email: "Email", longAddress: "Address", level: "Type", status: "Status", shippingMethod: "Shipping Method", shipAccountNumber: "Shipping Acct #", fabricSamples: "Fabric Samples", finishSamples: "Finish Samples", requesterNotes: "Requester Notes" };
              const label = labels[cellProps.field] || "";
              return React.cloneElement(cell, { ...cell.props, "data-label": label });
            }}
          >
            <GridToolbar>
              <button className="k-button k-button-md k-rounded-md k-button-solid" style={{ backgroundColor: "#333", color: "#fff", borderColor: "#333" }} onClick={this.onExportExcel}>
                <span className="k-icon k-i-file-excel"></span>&nbsp;Export to Excel
              </button>
              <button className="k-button k-button-md k-rounded-md k-button-solid" style={{ backgroundColor: "#333", color: "#fff", borderColor: "#333", marginLeft: "8px" }} onClick={this.onExportSummary}>
                <span className="k-icon k-i-file-excel"></span>&nbsp;Export Cuttings Summary
              </button>
              <button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base" onClick={this.onRefresh} style={{ marginLeft: "auto" }}>
                <span className="k-icon k-i-reload"></span>&nbsp;Refresh Grid
              </button>
            </GridToolbar>
            <GridColumn
              field="orderId"
              title="Order #"
              width="110"
              cell={(props) => (
                <td data-label="Order #">
                  {/* Order # opens the admin Sample Order Detail (fulfillment
                      management); the public order page is linked from there. */}
                  <a href={"/Admin/FabricSampleOrders/" + props.dataItem.id} style={{ textDecoration: "underline" }}>
                    {props.dataItem.orderId || "—"}
                  </a>
                </td>
              )}
            />
            <GridColumn field="dateString" title="Date" width="150" sortable={true} />
            <GridColumn field="name" title="Name" width="130" />
            {/* client 2026-10-16 (ticket 86e3re32m): company + Fairfield
                account number in one column */}
            <GridColumn field="companyAccount" title="Company / Account #" width="170" />
            <GridColumn field="email" title="Email" width="170" />
            <GridColumn field="longAddress" title="Address" width="200" />
            <GridColumn field="level" title="Type" width="100" />
            <GridColumn field="status" title="Status" width="100" />
            <GridColumn field="shippingMethod" title="Shipping Method" width="150" />
            <GridColumn field="shipAccountNumber" title="Shipping Acct #" width="130" />
            {/* Fixed width: as the grid's only flexible column this got
                starved to a sliver (fixed columns exceed the container),
                wrapping long lists one word per line — 800px-tall rows.
                Fixed widths everywhere = sane wrapping + horizontal scroll. */}
            <GridColumn field="fabricSamples" title="Fabric Samples" width="260" />
            <GridColumn field="finishSamples" title="Finish Samples" width="180" />
            {/* Preview only (client 2026-10-02): the Fabric Samples column
                keeps driving row height, so long notes CLAMP to the row via
                maxHeight + overflow hidden — full text on the detail page. */}
            <GridColumn field="requesterNotes" title="Requester Notes" width="220"
              cell={(props) => (
                <td data-label="Requester Notes">
                  <div style={{ maxHeight: "72px", overflow: "hidden" }}>{props.dataItem.requesterNotes}</div>
                </td>
              )} />
          </Grid>
        </ExcelExport>
      </div>
    );
  }
}
